// Temporary OFF path from main 072fa6b8c. Remove with the GDPR rollout flag.
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '@/components/ui/message-scroller';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  conversationStatusLabels,
  useAssignConversation,
  useCloseConversation,
  useCustomerTimeline,
  useDeleteConversation,
  useGetConversation,
  useMarkConversationRead,
  useSendMessage,
} from '@/features/conversations/api';
import type {
  Conversation,
  ConversationDetail as LoadedConversation,
  MessageTransport,
  MessagingPlatform,
} from '@/features/conversations/api';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  LoaderCircle,
  MoreHorizontal,
  Send,
  Trash2,
  UserCheck,
  XCircle,
} from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useActiveOrganization,
  useGetOrganizationMembers,
} from '@/features/organization';
import { useResolvedRoutes } from '@/lib/use-routes';
import { Link } from '@tanstack/react-router';

import { useConversationCalls } from '@/features/voice-receptionist/api';
import { AdReferralCard } from './ad-referral-card';
import { CallCard } from './call-card';
import { ConversationHandlerBadge } from './conversation-handler-badge';
import { ConversationMobileDetail } from './conversation-mobile-detail.legacy';
import {
  anchorCalls,
  callSidOf,
  callTranscriptPreviews,
  conversationHandoff,
  conversationLeadId,
  isLeadFormSubmission,
  lastAgentReplyAt,
  openTeamFollowUps,
} from './conversation-thread-events';
import { DaySeparatorBadge, dayKey, formatDayLabel } from './day-separator';
import { HandoffCard } from './handoff-card';
import { LeadFormCard } from './lead-form-card';
import { MessageBubble } from './message-bubble';
import { findLastOutboundIndex } from './message-delivery-status';
import { PhoneThreadActions } from './phone-thread-actions';
import { PlatformBadge } from './platform-badge';
import {
  ChannelSwitch,
  ReplyChannelPicker,
  useReplyTarget,
} from './reply-channel';
import { TeamFollowUpCard } from './team-follow-up-card';
import { useConversationIdentity } from './use-conversation-identity';

function memberFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/**
 * Why the reply composer is disabled, for both the desktop and mobile detail
 * views. Sends are already blocked server-side either way (a closed
 * conversation, or `checkSmsSendPolicy`'s suppression check for an
 * opted-out SMS contact) — this just makes the reason visible before staff
 * try, rather than after a send silently fails.
 */
export type ComposerDisabledReason =
  | 'closed'
  | 'opted_out'
  | 'phone_call'
  | null;

export function composerDisabledMessage(
  reason: ComposerDisabledReason
): string {
  if (reason === 'phone_call') return 'Continue this conversation by text.';
  return reason === 'opted_out'
    ? 'This contact opted out of SMS (replied STOP). Replies here are blocked.'
    : 'This conversation is closed.';
}

function ConversationHeader({
  conversation,
  memberFirstNameById,
  currentUserId,
  onDeleted,
  onBack,
  showInlineBackButton = true,
}: {
  conversation: LoadedConversation;
  memberFirstNameById: Map<string, string>;
  currentUserId?: string;
  onDeleted?: () => void;
  onBack?: () => void;
  showInlineBackButton?: boolean;
}) {
  const { closeConversation, isClosing } = useCloseConversation();
  const { assignConversation, isAssigning } = useAssignConversation();
  const { deleteConversation, isDeleting } = useDeleteConversation({
    onSuccess: onDeleted,
  });
  const routes = useResolvedRoutes();

  const isOpen =
    conversation.status !== 'closed' && conversation.status !== 'expired';
  const isAssignedToMe = conversation.assignedToId === currentUserId;
  const { displayName, formName } = useConversationIdentity(conversation);
  // The FK, falling back to the older metadata pointer: lead-form conversations
  // carry the FK, older ones only the pointer.
  const leadId = conversationLeadId(conversation);

  // Avatar + name + platform cluster. When the conversation is linked to a
  // lead, the whole cluster becomes a hover-shaded hit area that opens the
  // customer's page — not a button-looking control.
  const identityContent = (
    <>
      <Avatar className="size-8 shrink-0">
        {conversation.externalUserAvatar && (
          <AvatarImage src={conversation.externalUserAvatar} />
        )}
        <AvatarFallback className="text-xs">
          {displayName.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 text-left">
        <p className="truncate text-[15px] font-semibold">{displayName}</p>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          {formName ? (
            <>
              <span className="truncate">{formName}</span>
              <span aria-hidden>·</span>
            </>
          ) : null}
          {conversation.status === 'bot_handling' ||
          conversation.status === 'agent_handling' ? (
            <ConversationHandlerBadge
              conversation={conversation}
              memberFirstNameById={memberFirstNameById}
            />
          ) : (
            <span className="shrink-0">
              {conversationStatusLabels[conversation.status]}
            </span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="shrink-0 border-b bg-card px-5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-36 flex-1 items-center gap-3">
          {onBack && showInlineBackButton ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 shrink-0 rounded-full"
              aria-label="Back to conversations"
              onClick={onBack}
            >
              <ArrowLeft className="size-5" />
            </Button>
          ) : null}
          {leadId ? (
            <Link
              to={routes.customerDetail(leadId)}
              title="View customer"
              className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted"
            >
              {identityContent}
            </Link>
          ) : (
            <div className="flex min-w-0 items-center gap-3">
              {identityContent}
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1">
          <PhoneThreadActions
            key={conversation.id}
            conversationId={conversation.id}
            leadId={leadId}
            compact
            className="shrink-0"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 rounded-lg"
                aria-label="Conversation actions"
                title="Conversation actions"
              >
                <MoreHorizontal className="size-5" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isOpen && !isAssignedToMe && currentUserId ? (
                <DropdownMenuItem
                  disabled={isAssigning}
                  onSelect={() =>
                    assignConversation({
                      id: conversation.id,
                      assignToUserId: currentUserId,
                    })
                  }
                >
                  <UserCheck className="size-4" />
                  {isAssigning ? 'Assigning...' : 'Take Over'}
                </DropdownMenuItem>
              ) : null}
              {isOpen ? (
                <DropdownMenuItem
                  disabled={isClosing}
                  onSelect={() => closeConversation(conversation.id)}
                >
                  <XCircle className="size-4" />
                  {isClosing ? 'Closing...' : 'Close'}
                </DropdownMenuItem>
              ) : null}
              {isOpen ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem
                variant="destructive"
                disabled={isDeleting}
                onSelect={() => deleteConversation(conversation.id)}
              >
                <Trash2 className="size-4" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

function MobileConversationView({
  conversation,
  conversationId,
  memberFirstNameById,
  memberFullNameById,
  memberImageById,
  composerDisabledReason,
  onBack,
}: {
  conversation: LoadedConversation;
  conversationId: string;
  memberFirstNameById: Map<string, string>;
  memberFullNameById: Map<string, string>;
  memberImageById: Map<string, string | null>;
  composerDisabledReason: ComposerDisabledReason;
  onBack?: () => void;
}) {
  const { messages, isLoading } = useCustomerTimeline(conversationId);
  const { sendMessage, isSending } = useSendMessage();
  const reply = useReplyTarget(conversationId);
  // Merged inbox: a customer reachable on another channel can be written to
  // even from their call thread, so the opened thread's own reason gives way.
  const replyElsewhere = reply.merged && reply.target !== null;

  return (
    <ConversationMobileDetail
      key={conversationId}
      conversation={conversation}
      messages={messages}
      memberFirstNameById={memberFirstNameById}
      memberFullNameById={memberFullNameById}
      memberImageById={memberImageById}
      isLoadingMessages={isLoading}
      composerDisabledReason={replyElsewhere ? null : composerDisabledReason}
      replyChannel={
        replyElsewhere && reply.target ? (
          <ReplyChannelPicker
            replyable={reply.replyable}
            target={reply.target}
            onChange={reply.setTarget}
          />
        ) : null
      }
      isSending={isSending}
      onSend={(content) =>
        sendMessage({
          conversationId:
            replyElsewhere && reply.target ? reply.target : conversationId,
          content,
        })
      }
      onBack={onBack}
    />
  );
}

function MessageThread({
  conversationId,
  conversation,
}: {
  conversationId: string;
  conversation: Conversation;
}) {
  const { messages, isLoading } = useCustomerTimeline(conversationId);
  // Merged inbox: mark where the conversation moves between channels.
  const multiChannel = new Set(messages.map((m) => m.platform)).size > 1;
  const { calls } = useConversationCalls(
    conversationId,
    conversation.platform === 'phone' ||
      Boolean(conversationLeadId(conversation)) ||
      messages.some((m) => m.platform === 'phone')
  );
  const { byMessageId: callAbove, trailing: trailingCalls } = anchorCalls(
    messages,
    calls
  );
  const transcriptPreviews = callTranscriptPreviews(messages);
  const callIds = new Set(calls.map((call) => call.id));

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn('h-12 w-2/3', i % 2 === 1 && 'ml-auto')}
          />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-3 p-4">
        <AdReferralCard metadata={conversation.metadata} />
        {trailingCalls.map((call) => (
          <CallCard
            key={call.id}
            call={call}
            previewLines={transcriptPreviews.get(call.id)}
          />
        ))}
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-muted-foreground">
            No messages yet in this conversation.
          </p>
        </div>
      </div>
    );
  }

  const leadId = conversationLeadId(conversation);
  const handoff = conversationHandoff(conversation);
  const teamFollowUps = openTeamFollowUps(
    conversation,
    lastAgentReplyAt(messages)
  );
  const lastSystemIndex = messages.reduce(
    (found, m, i) => (m.role === 'system' && !callSidOf(m) ? i : found),
    -1
  );
  const lastOutboundIndex = findLastOutboundIndex(messages);
  // A hand-off with no system message to anchor to (a lead form Claire is not
  // following up) sits just under the form card instead.
  const showTrailingHandoff = Boolean(handoff) && lastSystemIndex === -1;

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="end">
      <MessageScroller className="min-h-0 flex-1">
        <MessageScrollerViewport>
          <MessageScrollerContent className="gap-5 px-5 py-6">
            <MessageScrollerItem>
              <AdReferralCard metadata={conversation.metadata} />
            </MessageScrollerItem>
            {leadId ? (
              <MessageScrollerItem>
                <LeadFormCard leadId={leadId} />
              </MessageScrollerItem>
            ) : null}
            {showTrailingHandoff && handoff ? (
              <MessageScrollerItem>
                <HandoffCard
                  reason={handoff.reason}
                  detail={handoff.detail}
                  at={handoff.at}
                />
              </MessageScrollerItem>
            ) : null}
            {messages.map((msg, index) => {
              if (isLeadFormSubmission(msg)) return null;
              const iso = msg.sentAt ?? msg.createdAt;
              const prev = index > 0 ? messages[index - 1] : null;
              const prevIso = prev ? (prev.sentAt ?? prev.createdAt) : null;
              const showDayBadge = !prevIso || dayKey(prevIso) !== dayKey(iso);
              const callsHere = callAbove.get(msg.id) ?? [];
              const transcriptInCard = callIds.has(callSidOf(msg) ?? '');
              if (transcriptInCard) return null;
              const channelChanges =
                multiChannel && (!prev || prev.platform !== msg.platform);
              return (
                <MessageScrollerItem
                  key={msg.id}
                  messageId={msg.id}
                  scrollAnchor={msg.role === 'user'}
                  className="flex flex-col gap-3"
                >
                  {showDayBadge ? (
                    <DaySeparatorBadge label={formatDayLabel(new Date(iso))} />
                  ) : null}
                  {channelChanges ? (
                    <ChannelSwitch
                      platform={msg.platform}
                      transport={msg.metadata?.transport}
                    />
                  ) : null}
                  {callsHere.map((call) => (
                    <CallCard
                      key={call.id}
                      call={call}
                      previewLines={transcriptPreviews.get(call.id)}
                    />
                  ))}
                  {msg.role === 'system' &&
                  index === lastSystemIndex &&
                  handoff ? (
                    <HandoffCard
                      reason={handoff.reason}
                      detail={handoff.detail}
                      at={handoff.at ?? iso}
                    />
                  ) : (
                    <MessageBubble
                      message={msg}
                      showReceipt={index === lastOutboundIndex}
                    />
                  )}
                </MessageScrollerItem>
              );
            })}
            {trailingCalls.map((call) => (
              <MessageScrollerItem key={call.id}>
                <CallCard
                  call={call}
                  previewLines={transcriptPreviews.get(call.id)}
                />
              </MessageScrollerItem>
            ))}
            {teamFollowUps.length > 0 ? (
              <MessageScrollerItem>
                <TeamFollowUpCard requests={teamFollowUps} />
              </MessageScrollerItem>
            ) : null}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}

function MessageInput({
  conversationId,
  disabledReason,
  leadId,
  platform,
  transport,
}: {
  conversationId: string;
  disabledReason: ComposerDisabledReason;
  leadId: string | null;
  platform: MessagingPlatform;
  transport: MessageTransport | null;
}) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { sendMessage, isSending } = useSendMessage({
    onSuccess: () => {
      setText('');
      textareaRef.current?.focus();
    },
  });

  const reply = useReplyTarget(conversationId);
  // Merged inbox: a reply goes to the channel picked (default: where the
  // customer last wrote), which can be reached even from their call thread.
  const replyElsewhere = reply.merged && reply.target !== null;
  const sendTo = replyElsewhere && reply.target ? reply.target : conversationId;
  const reason = replyElsewhere ? null : disabledReason;

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isSending || reason) return;

    sendMessage({ conversationId: sendTo, content: trimmed });
  }, [text, isSending, reason, sendTo, sendMessage]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="shrink-0 bg-card px-5 pb-4 pt-2">
      {reason === 'phone_call' ? (
        <div className="flex flex-col items-center gap-2">
          <p className="text-center text-sm text-muted-foreground">
            {composerDisabledMessage('phone_call')}
          </p>
          <PhoneThreadActions
            key={conversationId}
            conversationId={conversationId}
            leadId={leadId}
            messageOnly
          />
        </div>
      ) : reason ? (
        <p className="text-center text-sm text-muted-foreground">
          {composerDisabledMessage(reason)}
        </p>
      ) : (
        <div>
          <div className="overflow-hidden rounded-xl border bg-background transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10">
            <Textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label="Message"
              placeholder="Write a message…"
              rows={1}
              className="min-h-14 max-h-44 resize-none rounded-none border-0 bg-transparent px-3.5 pb-1 pt-3 shadow-none focus-visible:ring-0"
              disabled={isSending}
            />
            <div className="flex items-center justify-between gap-3 px-3 pb-3">
              {reply.target ? (
                <ReplyChannelPicker
                  replyable={reply.replyable}
                  target={reply.target}
                  onChange={reply.setTarget}
                />
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  Reply via{' '}
                  <PlatformBadge
                    platform={platform}
                    transport={transport}
                    className="py-0.5 text-[11px]"
                  />
                </div>
              )}

              <Button
                size="sm"
                className="h-9 rounded-lg px-3"
                onClick={handleSend}
                disabled={!text.trim() || isSending}
              >
                {isSending ? (
                  <LoaderCircle
                    className="size-4 motion-safe:animate-spin"
                    aria-hidden
                  />
                ) : (
                  <Send className="size-4" aria-hidden />
                )}
                {isSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ConversationDetailProps {
  conversationId: string;
  currentUserId?: string;
  onDeleted?: () => void;
  onBack?: () => void;
  /** When false, back is handled by the mobile dashboard header instead. */
  showInlineBackButton?: boolean;
  variant?: 'sidebar' | 'mobile';
}

export function ConversationDetail({
  conversationId,
  currentUserId,
  onDeleted,
  onBack,
  showInlineBackButton = true,
  variant = 'sidebar',
}: ConversationDetailProps) {
  const { conversation, isLoading, isError } =
    useGetConversation(conversationId);
  const { markConversationRead } = useMarkConversationRead();

  // Opening a thread marks it read for the whole team. Keyed on the latest
  // message time so a customer message arriving while the thread is open is
  // marked too, and gated on the tab being visible so a background tab can't
  // silently clear messages nobody has seen.
  const lastMessageAt = conversation?.lastMessageAt ?? null;
  const markedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversation) return;
    const key = `${conversationId}:${lastMessageAt ?? ''}`;
    const markIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (markedKeyRef.current === key) return;
      markedKeyRef.current = key;
      markConversationRead(conversationId);
    };
    markIfVisible();
    document.addEventListener('visibilitychange', markIfVisible);
    return () =>
      document.removeEventListener('visibilitychange', markIfVisible);
  }, [conversation, conversationId, lastMessageAt, markConversationRead]);

  const { data: activeOrg } = useActiveOrganization();
  const { members } = useGetOrganizationMembers(activeOrg?.id ?? '');

  const memberFirstNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      map.set(member.user.id, memberFirstName(member.user.name));
    }
    return map;
  }, [members]);

  const memberFullNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      map.set(member.user.id, member.user.name);
    }
    return map;
  }, [members]);

  const memberImageById = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const member of members) {
      map.set(member.user.id, member.user.image ?? null);
    }
    return map;
  }, [members]);

  if (isLoading) {
    return (
      <div
        className="flex flex-1 flex-col gap-5 bg-card p-5"
        role="status"
        aria-label="Loading conversation"
      >
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="mt-6 h-24 w-4/5" />
        <Skeleton className="ml-auto h-16 w-3/5" />
      </div>
    );
  }

  if (isError || !conversation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-white p-6">
        <p className="text-center text-sm text-muted-foreground">
          Could not load this conversation.
        </p>
        {onBack ? (
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            Back to inbox
          </Button>
        ) : null}
      </div>
    );
  }

  const isOpen =
    conversation.status !== 'closed' && conversation.status !== 'expired';
  const composerDisabledReason: ComposerDisabledReason =
    conversation.platform === 'phone'
      ? 'phone_call'
      : !isOpen
        ? 'closed'
        : conversation.isOptedOut
          ? 'opted_out'
          : null;

  if (variant === 'mobile') {
    return (
      <MobileConversationView
        conversation={conversation}
        conversationId={conversationId}
        memberFirstNameById={memberFirstNameById}
        memberFullNameById={memberFullNameById}
        memberImageById={memberImageById}
        composerDisabledReason={composerDisabledReason}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <ConversationHeader
        conversation={conversation}
        memberFirstNameById={memberFirstNameById}
        currentUserId={currentUserId}
        onDeleted={onDeleted}
        onBack={onBack}
        showInlineBackButton={showInlineBackButton}
      />
      <MessageThread
        conversationId={conversationId}
        conversation={conversation}
      />
      <MessageInput
        key={conversationId}
        conversationId={conversationId}
        disabledReason={composerDisabledReason}
        leadId={conversationLeadId(conversation)}
        platform={conversation.platform}
        transport={conversation.transport}
      />
    </div>
  );
}
